import { _getLogger } from '@doxyme/analytics';
import { Specs } from '@doxyme/extensions-common';
import ExtensionCapability from '../../capability';
import type ExtensionInstance from '../../instance';
import { logErrorAndThrowBind, type LogErrorAndThrowBound } from '../../utils';

const logger = _getLogger('receiveContext', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

type Constraints = Specs.ReceiveContext.Constraints;
type AppContext = Specs.ReceiveContext.UpdatePayload;

const { ReceiveContext } = Specs;

/**
 * Receive Context allows extensions to receive the host application's context.
 * This is useful for extensions that need to know the role of the user, the clinic ID,
 * or other information about the provider, patient, or session.
 */
export default class ReceiveContextCapability extends ExtensionCapability<Constraints> {
  static readonly id = ReceiveContext.id;

  private context?: AppContext = undefined;

  constructor() {
    super(ReceiveContext);

    this.handlers.push([
      ReceiveContext.Actions.Request,
      this.requestContext.bind(this),
    ]);
  }

  // Re-sends a cached context to the specify instance
  private requestContext(
    payload: never,
    instance: ExtensionInstance<Constraints>,
  ): void {
    if (!this.context) {
      logger.error(
        '[requestContext] Extension requested context but context is not set',
      );
      return;
    }

    const context = this.refineContext(instance, this.context);
    instance.notify([ReceiveContext.Events.Update, context]);
  }

  /**
   * Update the application context and notify all extensions.
   * Please note that context is tightly defined by the {@link Specs.ReceiveContext} Spec
   * with an acceptable list of props and all extraneous data will be dropped before being sent to extensions.
   * @param payload - The application context
   * @example
   * ```ts
   * updateContext({
   *  role: 'Provider',
   *  username: 'name',
   *  clinicId: '1234',
   *  theme: 'light',
   *  personalRoomUrl: 'doxy.me/room',
   *  subscriptionType: 'pro',
   *  userId: '4321',
   *  sessionId: 'VonageSessionId',
   *  ip: '127.0.0.1'
   * });
   * ```
   */
  updateContext(payload: AppContext): void {
    if (!ReceiveContext.isValidContext(payload)) {
      logErrorAndThrow('[updateContext] Invalid application context');
    }

    this.context = payload;

    for (const instance of Object.values(this.instances)) {
      const context = this.refineContext(instance, payload);
      instance.notify([ReceiveContext.Events.Update, context]);
    }
  }
}
